import { useEffect, useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { CellValueChangedEvent } from 'ag-grid-enterprise'
import { FileDown, FileUp, LayoutTemplate } from 'lucide-react'

import { downloadCSV, getCSVString } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@fortem-monorepo/react-ui'
import { FormItem } from '@fortem-monorepo/react-ui'
import { Label } from '@fortem-monorepo/react-ui'
import { DataUpload } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { UploadDataModal } from '@/components/dialogs/UploadDataModal'
import { TRADING } from '@/constants/links'
import { useUpdateFundsMetadataMutation } from '@/react-query/mutations/data-upload'
import { useFunds } from '@/react-query/queries/funds/funds'

const colDefs = createColDefs([
  {
    field: 'fund_id',
    headerName: 'fund_id',
    width: 115,
    hide: true,
  },
  {
    field: 'agg_scope',
    width: 200,
    rowGroup: true,
    hide: true,
  },
  {
    field: 'scope',
    width: 300,
  },
  {
    field: 'description',
    width: 300,
    cellStyle: {
      textAlign: 'center',
    },
  },
  {
    field: 'value',
    width: 150,
    editable: true,
    cellStyle: {
      textAlign: 'center',
    },
  },
])

export interface DataUploadPageProps {}

export const DataUploadPage: React.FC<DataUploadPageProps> = ({}) => {
  const [isUploading, setUploading] = useState(false)
  const [fund_name, setFundName] = useState('')

  const updateFundsMetadataMutation = useUpdateFundsMetadataMutation()

  const { data: funds, query: fundsQuery } = useFunds()

  const activeFunds = useMemo(() => funds.filter((fund) => fund.active), [funds])

  const downloadLiquidityReportTemplate = () => {
    const exampleData = [
      {
        fund_name: 'FCPGF',
        bucket: 'Beta',
        value: 0.01,
      },
      {
        fund_name: 'FCPGF',
        bucket: 'Protection',
        value: 0.025,
      },
      {
        fund_name: 'FCARF',
        bucket: 'Beta',
        value: 0.003,
      },
    ]

    downloadCSV(getCSVString(exampleData), 'liquidity-report-template.csv')
  }

  const downloadFundRatingTemplate = () => {
    const exampleData = [
      {
        fund_name: 'FCPGF',
        rating: 'AA',
      },
      {
        fund_name: 'FCDGF',
        rating: 'AAA',
      },
      {
        fund_name: 'FCARF',
        rating: 'AAA',
      },
    ]

    downloadCSV(getCSVString(exampleData), 'fund-rating-template.csv')
  }

  const api = useApi()

  const metadataQuery = useQuery({
    queryKey: ['data-upload', 'metadata', fund_name],
    queryFn: () => api.dataUpload.getFundsMetadata({ fund_name }),
    enabled: !!fund_name,
  })

  const queriesAreLoading = fundsQuery.isLoading || metadataQuery.isLoading

  useEffect(() => {
    if (!fund_name && activeFunds.length) {
      setFundName(activeFunds[0].fund_name)
    }
  }, [activeFunds])

  return (
    <Page metadata={{ title: 'Data Upload' }} permissions={TRADING.DATA_UPLOAD.permissions}>
      <UploadDataModal open={isUploading} onOpenChange={setUploading} />
      <HeaderConfigBar>
        <div className="flex items-end gap-2">
          <FormItem>
            <Label>Fund</Label>
            <SearchSelect
              size="sm"
              value={fund_name}
              setValue={setFundName}
              loading={fundsQuery.isFetching}
              options={activeFunds.map((fund) => ({
                label: fund.fund_name,
                value: fund.fund_name,
              }))}
            />
          </FormItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant={'outline'}>
                <FileDown />
                Download Template
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => downloadLiquidityReportTemplate()}>
                <LayoutTemplate />
                Liquidity Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadFundRatingTemplate()}>
                <LayoutTemplate />
                Rating
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant={'outline'} onClick={() => setUploading(true)}>
            <FileUp />
            Upload
          </Button>
        </div>
      </HeaderConfigBar>
      <Widget exposed className={'h-full'}>
        <WidgetHeaderWithQuery title={`Funds Metadata - ${fund_name}`} query={metadataQuery} />
        <WidgetContent loading={queriesAreLoading}>
          <CustomAgGrid
            className={'min-h-[800px]'}
            domLayout={'normal'}
            query={metadataQuery}
            onCellValueChanged={(
              params: CellValueChangedEvent<DataUpload.GetFundsMetdataData[number]>,
            ) => {
              return updateFundsMetadataMutation.mutateAsync({
                newValue: params.newValue,
                oldValue: params.oldValue,
                rowData: params.data,
                updatedField: params.colDef.field!,
              })
            }}
            rowData={metadataQuery.data ?? []}
            columnDefs={colDefs}
          />
        </WidgetContent>
      </Widget>
    </Page>
  )
}
